import React, { useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { Logo } from '../atoms/Logo';
import { Link } from '../atoms/Link';
import { PageRoutes } from '../../constants/routes';
import { useLanguage } from '../../contexts/LanguageContext';

export const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [propertyDropdownOpen, setPropertyDropdownOpen] = useState(false);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const [dropdownTimeout, setDropdownTimeout] = useState<NodeJS.Timeout | null>(null);
  const location = useLocation();
  const { t, currentLanguage, changeLanguage } = useLanguage();

  const handleLanguageChange = async (language: string) => {
    setIsChangingLanguage(true);
    try {
      await changeLanguage(language);
    } catch (error) {
      console.error('Error changing language:', error);
    } finally {
      setIsChangingLanguage(false);
    }
  };

  // Helper function to check if a route is active
  const isActiveRoute = (route: string) => {
    if (route === PageRoutes.HOME) {
      return location.pathname === route;
    }
    return location.pathname.startsWith(route);
  };

  // Helper function to check if property dropdown should be active
  const isPropertyActive = () => {
    return location.pathname.startsWith('/property');
  };

  return (
    <HeaderWrapper>
      <LogoArea>
        <Link to={PageRoutes.HOME}>
          <Logo height={70} width={156} />
        </Link>
      </LogoArea>
      <StyledNav>
        <NavMenu>
          <NavItem as={Link} to={PageRoutes.HOME} $active={isActiveRoute(PageRoutes.HOME)}>{t('header.home')}</NavItem>
          <NavItem
            onMouseEnter={() => {
              if (dropdownTimeout) {
                clearTimeout(dropdownTimeout);
                setDropdownTimeout(null);
              }
              setPropertyDropdownOpen(true);
            }}
            onMouseLeave={() => {
              const timeout = setTimeout(() => setPropertyDropdownOpen(false), 150);
              setDropdownTimeout(timeout);
            }}
            style={{ position: 'relative' }}
            $active={isPropertyActive()}
          >
            <Link to={PageRoutes.PROPERTY.RESIDENTIAL} style={{ display: 'flex', alignItems: 'center' }}>
              {t('header.properties')} <DropdownArrow>▼</DropdownArrow>
            </Link>
            {propertyDropdownOpen && (
              <DropdownMenu
                onMouseEnter={() => {
                  if (dropdownTimeout) {
                    clearTimeout(dropdownTimeout);
                    setDropdownTimeout(null);
                  }
                  setPropertyDropdownOpen(true);
                }}
                onMouseLeave={() => {
                  const timeout = setTimeout(() => setPropertyDropdownOpen(false), 150);
                  setDropdownTimeout(timeout);
                }}
              >
                <DropdownMenuItem as={Link} to={PageRoutes.PROPERTY.RESIDENTIAL} $active={location.pathname === PageRoutes.PROPERTY.RESIDENTIAL}>{t('header.residential')}</DropdownMenuItem>
                <DropdownMenuItem as={Link} to={PageRoutes.PROPERTY.COMMERCIAL} $active={location.pathname === PageRoutes.PROPERTY.COMMERCIAL}>{t('header.commercial')}</DropdownMenuItem>
              </DropdownMenu>
            )}
          </NavItem>
          <NavItem as={Link} to={PageRoutes.BLOG} $active={isActiveRoute(PageRoutes.BLOG)}>{t('header.blog')}</NavItem>
          <NavItem as={Link} to={PageRoutes.ABOUT} $active={isActiveRoute(PageRoutes.ABOUT)}>{t('header.about')}</NavItem>
          <NavItem as={Link} to={PageRoutes.CONTACT} $active={isActiveRoute(PageRoutes.CONTACT)}>{t('header.contact')}</NavItem>
        </NavMenu>
        <StyledLang>
          <Flag
            role="img"
            aria-label={t('languages.khmer')}
            onClick={() => handleLanguageChange('kh')}
            $active={currentLanguage === 'kh' || currentLanguage.startsWith('kh')}
            $disabled={isChangingLanguage}
          >
            🇰🇭
          </Flag>
          <Flag
            role="img"
            aria-label={t('languages.english')}
            onClick={() => handleLanguageChange('en')}
            $active={currentLanguage === 'en' || currentLanguage.startsWith('en')}
            $disabled={isChangingLanguage}
          >
            🇬🇧
          </Flag>
          <Flag
            role="img"
            aria-label={t('languages.chinese')}
            onClick={() => handleLanguageChange('zh')}
            $active={currentLanguage === 'zh' || currentLanguage.startsWith('zh')}
            $disabled={isChangingLanguage}
          >
            🇨🇳
          </Flag>
        </StyledLang>
        <Hamburger onClick={() => setMenuOpen(!menuOpen)}>
          <span />
          <span />
          <span />
        </Hamburger>
      </StyledNav>
      {menuOpen && (
        <MobileMenuOverlay onClick={() => setMenuOpen(false)}>
          <MobileMenu onClick={e => e.stopPropagation()}>
            <CloseBtn onClick={() => setMenuOpen(false)}>&times;</CloseBtn>
            <MobileNavMenu>
              <NavItem as={Link} to={PageRoutes.HOME} onClick={() => setMenuOpen(false)} $active={isActiveRoute(PageRoutes.HOME)}>{t('header.home')}</NavItem>
              <NavItem style={{ position: 'relative' }} $active={false}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {t('header.properties')}
                </div>
                <DropdownMenuMobile>
                  <DropdownMenuItem as={Link} to={PageRoutes.PROPERTY.RESIDENTIAL} onClick={() => setMenuOpen(false)} $active={location.pathname === PageRoutes.PROPERTY.RESIDENTIAL}>{t('header.residential')}</DropdownMenuItem>
                  <DropdownMenuItem as={Link} to={PageRoutes.PROPERTY.COMMERCIAL} onClick={() => setMenuOpen(false)} $active={location.pathname === PageRoutes.PROPERTY.COMMERCIAL}>{t('header.commercial')}</DropdownMenuItem>
                </DropdownMenuMobile>
              </NavItem>
              <NavItem as={Link} to={PageRoutes.BLOG} onClick={() => setMenuOpen(false)} $active={isActiveRoute(PageRoutes.BLOG)}>{t('header.blog')}</NavItem>
              <NavItem as={Link} to={PageRoutes.ABOUT} onClick={() => setMenuOpen(false)} $active={isActiveRoute(PageRoutes.ABOUT)}>{t('header.about')}</NavItem>
              <NavItem as={Link} to={PageRoutes.CONTACT} onClick={() => setMenuOpen(false)} $active={isActiveRoute(PageRoutes.CONTACT)} $isLast={true}>{t('header.contact')}</NavItem>
            </MobileNavMenu>
            <MobileLangSwitcher>
              <Flag
                role="img"
                aria-label={t('languages.khmer')}
                onClick={() => { handleLanguageChange('kh'); setMenuOpen(false); }}
                $active={currentLanguage === 'kh' || currentLanguage.startsWith('kh')}
                $disabled={isChangingLanguage}
              >
                🇰🇭
              </Flag>
              <Flag
                role="img"
                aria-label={t('languages.english')}
                onClick={() => { handleLanguageChange('en'); setMenuOpen(false); }}
                $active={currentLanguage === 'en' || currentLanguage.startsWith('en')}
                $disabled={isChangingLanguage}
              >
                🇬🇧
              </Flag>
              <Flag
                role="img"
                aria-label={t('languages.chinese')}
                onClick={() => { handleLanguageChange('zh'); setMenuOpen(false); }}
                $active={currentLanguage === 'zh' || currentLanguage.startsWith('zh')}
                $disabled={isChangingLanguage}
              >
                🇨🇳
              </Flag>
            </MobileLangSwitcher>
          </MobileMenu>
        </MobileMenuOverlay>
      )}
    </HeaderWrapper>
  );
};

const HeaderWrapper = styled.header`
  background: #fff;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  display: flex;
  align-items: center;
  padding: 0 80px;
  height: 80px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  @media (max-width: 900px) {
    padding: 0 18px;
    height: 60px;
  }
`;

const LogoArea = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
`;

const StyledNav = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: 32px;
  position: relative;
`;

const NavMenu = styled.ul`
  display: flex;
  align-items: center;
  justify-content: space-between;
  list-style: none;
  margin: 0;
  padding: 0;
  @media (max-width: 900px) {
    display: none;
  }
`;

const NavItem = styled.div<{ $active?: boolean; $isLast?: boolean }>`
  font-size: 16px;
  color: #222;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  margin: 10px 14px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  text-decoration: none;
  
  /* Override Link component's underline when nested inside NavItem */
  a {
    text-decoration: none !important;
    color: inherit;
    
    &:hover {
      text-decoration: none !important;
    }
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: ${({ $active }) => ($active ? '100%' : '0px')};
    height: 2px;
    background: #18145a;
    border-radius: 1px;
    transition: width 0.2s ease;
  }
  
  &:hover {
    color: #18145a;
    text-decoration: none;
    
    &::after {
      width: 100%;
    }
  }
  
  @media (max-width: 900px) {
    margin: 0;
    padding: 12px 0;
    width: 100%;
    word-wrap: break-word;
    overflow-wrap: break-word;
    border-bottom: ${({ $isLast }) => ($isLast ? 'none' : '1px solid #f0f0f0')};
    
    &::after {
      display: none;
    }
    
    &:hover {
      border-bottom: ${({ $isLast }) => ($isLast ? 'none' : '1px solid #f0f0f0')};
    }
  }
`;

const DropdownArrow = styled.span`
  font-size: 12px;
  margin-left: 3px;
`;

const StyledLang = styled.div`
  display: flex;
  align-items: center;
  margin-left: 18px;
  @media (max-width: 900px) {
    display: none;
  }
`;

const Flag = styled.span<{ $active?: boolean; $disabled?: boolean }>`
  font-size: 18px;
  margin-left: 12px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $active, $disabled }) => {
    if ($disabled) return 0.3;
    return $active ? 1 : 0.5;
  }};
  transition: all 0.15s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: ${({ $active }) => ($active ? '16px' : '0px')};
    height: 2px;
    background: #18145a;
    border-radius: 1px;
    transition: width 0.15s ease;
  }
  
  &:hover {
    opacity: ${({ $disabled }) => ($disabled ? 0.3 : 0.8)};
    
    &::after {
      width: ${({ $active, $disabled }) => ($disabled ? '0px' : '12px')};
    }
  }
`;

const Hamburger = styled.button`
  display: none;
  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 36px;
    height: 36px;
    background: none;
    border: none;
    cursor: pointer;
    margin-left: 12px;
    z-index: 20;
    span {
      display: block;
      width: 24px;
      height: 3px;
      background: #18145a;
      margin: 3px 0;
      border-radius: 2px;
      transition: 0.3s;
    }
  }
`;

const MobileMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(24, 20, 90, 0.12);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
`;

const MobileMenu = styled.div`
  background: #fff;
  width: 260px;
  height: 100vh;
  box-shadow: -2px 0 8px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  padding: 32px 24px 24px 24px;
  position: relative;
  overflow-x: hidden;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 18px;
  background: none;
  border: none;
  font-size: 32px;
  color: #18145a;
  cursor: pointer;
`;

const MobileNavMenu = styled.div`
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;

  @media (max-width: 900px) {
    margin: 18px 0 0 0;
  }
`;

const MobileLangSwitcher = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid #e5e5e5;
`;

const DropdownMenu = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  border-radius: 8px;
  min-width: 200px;
  padding: 12px 0;
  z-index: 100;
  list-style: none;
  border: 1px solid #e5e5e5;
  margin-top: 0;
  padding-top: 8px;
`;

const DropdownMenuMobile = styled.ul`
  background: transparent;
  width: 100%;
  padding: 0;
  z-index: 100;
  list-style: none;
  margin: 0;
  margin-left: 20px;
`;

const DropdownMenuItem = styled.li<{ $active?: boolean }>`
  padding: 8px 0;
  font-size: 15px;
  color: #333;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  cursor: pointer;
  background: transparent;
  display: block;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.2s ease;
  
  &:hover {
    color: #18145a;
  }
  
  @media (min-width: 901px) {
    border-bottom: none;
    padding-bottom: 12px;
    padding: 12px 20px;
  }
`;